import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { apiPaths, authHeader, handleResponse } from '../../../queryClient';

export interface EditDialogProps {
  onClose: () => void;
  open: boolean;
  name?: string;
  phone?: string;
}

interface FormData {
  name: string;
  phone: string;
}

const EditDialog = ({ onClose, open, name, phone }: EditDialogProps) => {
  const { control, formState, handleSubmit, setError, reset } = useForm<FormData>({
    defaultValues: { name, phone },
  });

  const queryClient = useQueryClient();
  const queryKey = [{ path: apiPaths.me }];

  const { isLoading, mutate: editProfile } = useMutation<void, Response, FormData, { previousProfile: FormData }>({
    // @ts-ignore
    async mutationFn(payload) {
      return fetch(`${apiPaths.editProfile}`, {
        body: JSON.stringify(payload),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'POST',
      }).then(handleResponse);
    },
    async onMutate(payload) {
      const previousProfile = queryClient.getQueryData([{ path: apiPaths.me }])!;
      queryClient.setQueryData(queryKey, { ...previousProfile, name: payload.name, phone: payload.phone });

      return { previousProfile };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });

      onClose();
    },
    onError: async (error, payload, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousProfile);
      }

      setError('name', { message: 'Something went wrong.' });
    },
  });

  const onSubmit: SubmitHandler<FormData> = (payload, e) => {
    e?.preventDefault();

    editProfile(payload);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <TextField
                {...field}
                error={!!formState.errors.name}
                fullWidth
                helperText={formState.errors.name?.message}
                required
                label="Name"
                sx={{ mt: 1 }}
                inputProps={{ maxLength: 30 }}
              />
            )}
            rules={{ required: 'Please fill in a name' }}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <TextField
                {...field}
                error={!!formState.errors.phone}
                fullWidth
                type="number"
                helperText={formState.errors.phone?.message}
                label="Phone"
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              onClose();
              reset();
            }}
          >
            Cancel
          </Button>
          <LoadingButton loading={isLoading} type="submit" variant="contained">
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditDialog;
